import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { CRITICAL_SCORE, ScoreSeries } from '../lib/dashboard';

// Weekly dashboard (GRI-957): store score per week, one line per location (design prototype's
// "Store score" chart). Hovering a week shows its values; clicking or dragging selects it.

type Props = {
  series: ScoreSeries[];
  // X axis labels, oldest week first (same length as each series' values).
  weekLabels: string[];
  selected: number;
  onSelect: (index: number) => void;
};

const NARROW = '(max-width: 640px)';
const useNarrow = () => {
  const query = () =>
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia(NARROW).matches;
  const [narrow, setNarrow] = useState(query);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(NARROW);
    const onChange = () => setNarrow(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return narrow;
};

const StoreScoreChart = ({ series, weekLabels, selected, onSelect }: Props) => {
  const narrow = useNarrow();
  const [hover, setHover] = useState<number | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);

  const W = narrow ? 400 : 800;
  const H = 352;
  const n = weekLabels.length;
  const L = 40,
    R = W - 56,
    T = 16,
    B = H - 28,
    pad = 12;
  const step = n > 1 ? (R - L - 2 * pad) / (n - 1) : 0;
  const xAt = (i: number) => L + pad + i * step;

  const values = series
    .flatMap((s) => s.values)
    .filter((v): v is number => v !== null);
  const vMin = values.length ? Math.min(...values) : 0;
  const vMax = values.length ? Math.max(...values) : 100;
  const tStep = vMax - vMin <= 30 ? 10 : 20;
  const lo = Math.max(0, Math.floor(vMin / tStep) * tStep);
  let hi = Math.min(100, Math.ceil(vMax / tStep) * tStep);
  if (hi <= lo) hi = Math.min(100, lo + tStep);
  const yAt = (v: number) => T + ((hi - v) / (hi - lo || 1)) * (B - T);
  const ticks: number[] = [];
  for (let v = lo; v <= hi; v += tStep) ticks.push(v);

  const current = hover ?? selected;
  const dim = (s: ScoreSeries) => !!highlight && highlight !== s.locationId;
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;

  // End labels (location names) at each line's last point, nudged apart so they don't overlap.
  const ends = series
    .map((s) => {
      let i = n - 1;
      while (i >= 0 && s.values[i] === null) i--;
      return i < 0 ? null : { s, x: xAt(i), y: yAt(s.values[i] as number) };
    })
    .filter((e): e is { s: ScoreSeries; x: number; y: number } => e !== null)
    .sort((a, b) => a.y - b.y);
  for (let k = 1; k < ends.length; k++)
    if (ends[k].y - ends[k - 1].y < 16) ends[k].y = ends[k - 1].y + 16;
  // Keep them inside the plot: labels pushed below the X axis move the group back up.
  for (let k = ends.length - 1; k >= 0; k--) {
    const limit = k === ends.length - 1 ? B : ends[k + 1].y - 16;
    if (ends[k].y > limit) ends[k].y = limit;
  }

  const indexAt = (e: React.PointerEvent<SVGRectElement>) => {
    const box = e.currentTarget.ownerSVGElement!.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * W;
    return Math.max(
      0,
      Math.min(n - 1, Math.round((x - (L + pad)) / (step || 1)))
    );
  };

  const legend = series
    .map((s) => ({ s, v: s.values[current] }))
    .sort((a, b) => (b.v ?? -1) - (a.v ?? -1));

  return (
    <div>
      <ChartBox>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Store score per week for each location"
        >
          {ticks.map((v) => (
            <line
              key={v}
              x1={L}
              x2={R}
              y1={yAt(v)}
              y2={yAt(v)}
              stroke="#E1E2E8"
              strokeWidth={1}
            />
          ))}
          <line
            x1={xAt(current)}
            x2={xAt(current)}
            y1={T}
            y2={B}
            stroke="#43474E"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
          {series.map((s) => (
            <polyline
              key={s.locationId}
              points={s.values
                .map((v, i) => (v === null ? null : `${xAt(i)},${yAt(v)}`))
                .filter(Boolean)
                .join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={highlight === s.locationId ? 3 : 2}
              strokeOpacity={dim(s) ? 0.2 : 1}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
          {series
            .filter((s) => s.values[current] !== null)
            .map((s) => (
              <circle
                key={s.locationId}
                cx={xAt(current)}
                cy={yAt(s.values[current] as number)}
                r={5}
                fill={s.color}
                fillOpacity={dim(s) ? 0.2 : 1}
                stroke="#FFFFFF"
                strokeWidth={2}
              />
            ))}
          <rect
            data-testid="chart-hit-area"
            x={0}
            y={0}
            width={W}
            height={H}
            fill="transparent"
            style={{ cursor: 'crosshair' }}
            onPointerMove={(e) => setHover(indexAt(e))}
            onPointerLeave={() => setHover(null)}
            onPointerDown={(e) => onSelect(indexAt(e))}
          />
        </svg>
        {ticks.map((v) => (
          <AxisLabel
            key={`y${v}`}
            style={{
              left: pct(L - 8, W),
              top: pct(yAt(v), H),
              transform: 'translate(-100%, -50%)',
            }}
          >
            {v}
          </AxisLabel>
        ))}
        {weekLabels.map((label, i) => (
          <AxisLabel
            key={`x${i}`}
            style={{
              left: pct(xAt(i), W),
              top: pct(B + 14, H),
              transform: 'translate(-50%, -50%)',
              color: i === current ? '#191C20' : undefined,
              fontWeight: i === current ? 500 : undefined,
            }}
          >
            {/* Narrow screens: every other label, always the current one. */}
            {narrow && i !== current && (i - current) % 2 !== 0 ? '' : label}
          </AxisLabel>
        ))}
        {ends.map((e) => (
          <EndLabel
            key={e.s.locationId}
            aria-hidden="true"
            style={{
              left: pct(e.x + 6, W),
              top: pct(e.y, H),
              color: e.s.color,
              opacity: dim(e.s) ? 0.35 : 1,
            }}
          >
            {e.s.name}
          </EndLabel>
        ))}
      </ChartBox>
      <Legend role="group" aria-label="Highlight a location">
        {legend.map(({ s, v }) => {
          const on = highlight === s.locationId;
          return (
            <LegendItem
              key={s.locationId}
              aria-pressed={on}
              onClick={() => setHighlight(on ? null : s.locationId)}
              style={{
                background: on ? '#D7E3F8' : 'transparent',
                opacity: highlight && !on ? 0.45 : 1,
              }}
            >
              <span
                style={{
                  flex: 'none',
                  width: 16,
                  height: 3,
                  borderRadius: 2,
                  background: s.color,
                }}
              />
              <span className="name">Location {s.name}</span>
              <span className="bar">
                <span style={{ width: `${v ?? 0}%`, background: s.color }} />
              </span>
              <span
                className="value"
                style={{
                  color:
                    v !== null && v < CRITICAL_SCORE ? '#BA1A1A' : '#191C20',
                }}
              >
                {v === null ? '—' : v}
              </span>
            </LegendItem>
          );
        })}
      </Legend>
    </div>
  );
};

const ChartBox = styled.div`
  position: relative;
  margin-top: 8px;
  svg {
    width: 100%;
    height: auto;
    display: block;
    touch-action: pan-y;
  }
`;

const AxisLabel = styled.span`
  position: absolute;
  pointer-events: none;
  white-space: nowrap;
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: #43474e;
`;

const EndLabel = styled.span`
  position: absolute;
  transform: translateY(-50%);
  padding: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
  pointer-events: none;
  white-space: nowrap;
`;

const Legend = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  column-gap: 24px;
  margin-top: 12px;
`;

const LegendItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  font: inherit;
  cursor: pointer;
  .name {
    flex: 1;
    min-width: 0;
    text-align: left;
    font-size: 14px;
    line-height: 20px;
    color: #191c20;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .bar {
    position: relative;
    flex: none;
    width: 64px;
    height: 4px;
    border-radius: 4px;
    background: #e1e2e8;
    overflow: hidden;
    span {
      position: absolute;
      inset: 0 auto 0 0;
      border-radius: 4px;
    }
  }
  .value {
    flex: none;
    min-width: 40px;
    text-align: right;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }
`;

export default StoreScoreChart;
